import "./applications.scss";
import {
    type FC,
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
} from "react";
import { useDispatch } from "react-redux";
import { useParams } from "react-router";
import { useNavigate } from "react-router-dom";
import { Banner, BannerType, Popover } from "@onshopfront/components";
import { useVirtualizer } from "@tanstack/react-virtual";
import FontAwesomeLight from "../../../app/Utilities/Platform/FontAwesome/FontAwesomeLight";
import { hideDialog, showDialog } from "../../Actions/DialogActions";
import Button from "../../Common/Button";
import { useTitle } from "../../Utilities/TitleHook";
import ApplicationCallDetails from "./ApplicationCallDetails";
import { ApplicationHistoryLoad } from "./ApplicationHistoryLoad";

export interface ApplicationRequestLogType {
    type: undefined;
    method: string;
    status: number;
    user_agent: string;
    path: string;
    vendor: string;
    vendor_id: string;
    request_body: null | string;
    response_body: string;
    timestamp: number;
}

export type ApplicationLogType = ApplicationRequestLogType | {
    type: "webhook";
    uuid: string;
    action: "CREATE" | "ACTIVATE" | "DELETE" | "DEACTIVATE";
    performedBy: string;
    reason: string | null;
    timestamp: number;
};

export const ApplicationHistory: FC = () => {
    useTitle("Application History");

    const dispatch = useDispatch<any>();

    const loadingRef = useRef<null | string>(null);

    const { id } = useParams();
    const [ loading, setLoading ] = useState(true);
    const [ error, setError ] = useState("");
    const [ keepLoading, setKeepLoading ] = useState(false);

    const [ logs, setLogs ] = useState<Array<ApplicationLogType>>([]);
    const [ hasNextPage, setHasNextPage ] = useState(false);
    const [ nextPageError, setNextPageError ] = useState(false);
    const [ requestDetails, setRequestDetails ] = useState<{
        cursor: string | null;
        startTime: number | null;
        endTime: number | null;
        reachedMax: boolean;
    } | null>(null);

    const parentRef = useRef<HTMLDivElement>(null);
    const parentOffsetRef = useRef(0);
    const scrollRef = useRef<HTMLDivElement>(null);

    const navigate = useNavigate();

    const loadPage = useCallback(
        (pageCursor: string | null, startTime?: number | null, endTime?: number | null): void => {
            if(!pageCursor) {
                setLoading(true);
            } else if(loadingRef.current === pageCursor) {
                // Already loading the page
                return;
            }

            loadingRef.current = pageCursor;

            let params = "?";

            if(pageCursor) {
                params += `after=${pageCursor}&`;
            }

            if(startTime) {
                params += `startTime=${startTime}&`;
            }

            if(endTime) {
                params += `endTime=${endTime}&`;
            }

            params = params.slice(0, -1);

            fetch(`/application/${id}/history${params}`, {
                credentials: "same-origin",
                headers    : new Headers({
                    "Accept"      : "application/json",
                    "Content-Type": "application/json",
                }),
            })
                .then(async (response) => {
                    if(!pageCursor) {
                        setLoading(false);
                    }

                    setKeepLoading(false);

                    if(response.status !== 200) {
                        let responseBody;

                        try {
                            responseBody = await response.json();
                        } catch {
                            throw new Error("Unable to parse error from the server");
                        }

                        throw new Error(
                            responseBody.error_code ?
                                `${responseBody.error_code}: ${responseBody.message}` :
                                responseBody.message
                        );
                    }

                    return response.json();
                })
                .then(({ logs, endCursor, hasNextPage, startTime, endTime, maxAttempts }) => {
                    if(!pageCursor) {
                        setLogs(logs);
                    } else {
                        setLogs(prevState => [
                            ...prevState,
                            ...logs,
                        ]);
                    }

                    setHasNextPage(hasNextPage);
                    setRequestDetails({
                        cursor    : endCursor,
                        reachedMax: maxAttempts,
                        startTime,
                        endTime,
                    });
                })
                .catch((err: any) => {
                    if(!pageCursor) {
                        setError(err instanceof Error ? err.message : err.toString());
                    } else {
                        setNextPageError(true);
                    }
                });
        },
        [ id ]
    );

    // Load first page
    useEffect(() => {
        loadPage(null, null, null);
    }, [ loadPage ]);

    const loadNextPage = useCallback(() => {
        loadPage(requestDetails?.cursor ?? null, requestDetails?.startTime, requestDetails?.endTime);
    }, [ requestDetails, loadPage ]);

    useLayoutEffect(() => {
        parentOffsetRef.current = parentRef.current?.offsetTop ?? 0;
    }, []);

    const virtualizer = useVirtualizer({
        count           : logs.length + (hasNextPage ? 1 : 0),
        scrollMargin    : parentOffsetRef.current,
        estimateSize    : () => 30,
        getScrollElement: () => scrollRef.current,
        overscan        : 10,
    });

    const spinner = (
        <div>
            <p><FontAwesomeLight icon="spinner" pulse />Loading...</p>
        </div>
    );

    let content;

    if(error) {
        content = (
            <div>
                <Banner
                    type={BannerType.Error}
                >
                    {error}
                </Banner>
            </div>
        );
    } else if(loading) {
        content = spinner;
    } else if(!logs.length && requestDetails?.reachedMax) {
        content = (
            <div>
                {keepLoading && spinner}
                <a
                    href="#"
                    onClick={e => {
                        e.preventDefault();
                        setKeepLoading(true);
                        loadNextPage();
                    }}
                    className={keepLoading ? "hidden" : ""}
                >
                    No recent calls, keep loading?
                </a>
                <p>
                    This can happen when you don&apos;t make consistent API calls or there haven&apos;t been any
                    recently.
                </p>
                {requestDetails.cursor !== null && (
                    <p>Last Lookup Key: {btoa(requestDetails.cursor)}</p>
                )}
            </div>
        );
    } else if(!logs.length) {
        content = (
            <div>
                <p>No API calls to date...</p>
            </div>
        );
    } else {
        content = (
            <div ref={parentRef}>
                <div className="flex items-center flex-row uppercase font-bold gap-4">
                    <div style={{ flexBasis: 180 }} className="grow-0 shrink">
                        Created At (UTC)
                    </div>
                    <div style={{ flexBasis: 40 }} className="flex-grow-2 shrink">
                        Path
                    </div>
                    <div style={{ flexBasis: 80 }} className="grow-0 shrink">
                        Method
                    </div>
                    <div style={{ flexBasis: 70 }} className="grow-0 shrink">
                        Status
                    </div>
                    <div style={{ flexBasis: 160 }} className="flex-grow-1 shrink">
                        Vendor
                    </div>
                    <div style={{ flexBasis: 50 }} className="grow-0 shrink" />
                </div>
                <div
                    style={{
                        height  : virtualizer.getTotalSize(),
                        width   : "100%",
                        position: "relative",
                    }}
                >
                    {virtualizer.getVirtualItems().map(row => {
                        if(row.index >= logs.length) {
                            return (
                                <div
                                    key={row.key}
                                    style={{
                                        position : "absolute",
                                        top      : 0,
                                        left     : 0,
                                        width    : "100%",
                                        transform: `translateY(${row.start}px)`,
                                    }}
                                    className="flex items-center flex-row gap-4 h-[30px]"
                                >
                                    {nextPageError ? (
                                        <div className="text-red-600 w-full text-center">
                                            <FontAwesomeLight
                                                icon="exclamation-triangle"
                                                className="text-red-400 mr-2"
                                            />
                                            Failed to load the next page
                                        </div>
                                    ) : (
                                        <ApplicationHistoryLoad
                                            onLoad={loadNextPage}
                                        />
                                    )}
                                </div>
                            );
                        }

                        const logRow = logs[row.index];

                        if(logRow.type === "webhook") {
                            return (
                                <div
                                    key={row.key}
                                    style={{
                                        position : "absolute",
                                        top      : 0,
                                        left     : 0,
                                        width    : "100%",
                                        transform: `translateY(${row.start}px)`,
                                    }}
                                    className={
                                        "flex items-center flex-row gap-4 h-[30px] " +
                                        (
                                            logRow.action === "ACTIVATE" || logRow.action === "CREATE" ?
                                                "bg-green-100 dark:bg-green-900" :
                                                "bg-red-100 dark:bg-red-900"
                                        )
                                    }
                                >
                                    <div style={{ flexBasis: 180 }} className="grow-0 shrink">
                                        {logRow.timestamp}
                                    </div>
                                    <Popover
                                        location="top"
                                        content={logRow.reason}
                                        disabled={!logRow.reason}
                                    >
                                        {(ref, getProps) => (
                                            <div
                                                className={
                                                    "text-ellipsis whitespace-nowrap w-full text-center flex-1 " +
                                                    (logRow.reason ? "cursor-pointer" : "")
                                                }
                                                ref={ref}
                                                {...getProps()}
                                            >
                                                Webhook Event: {logRow.action} on {logRow.uuid} by user{" "}
                                                {logRow.performedBy}
                                            </div>
                                        )}
                                    </Popover>
                                    <div style={{ flexBasis: 50 }} className="actions grow-0 shrink" />
                                </div>
                            );
                        }

                        return (
                            <div
                                key={row.key}
                                style={{
                                    position : "absolute",
                                    top      : 0,
                                    left     : 0,
                                    width    : "100%",
                                    transform: `translateY(${row.start}px)`,
                                }}
                                className="flex items-center flex-row gap-4 h-[30px]"
                            >
                                <div style={{ flexBasis: 180 }} className="grow-0 shrink">
                                    {logRow.timestamp}
                                </div>
                                <div
                                    style={{ flexBasis: 40 }}
                                    className={
                                        "history-url grow-2 shrink text-ellipsis whitespace-nowrap"
                                    }
                                >
                                    {logRow.path}
                                </div>
                                <div style={{ flexBasis: 80 }} className="grow-0 shrink">
                                    {logRow.method}
                                </div>
                                <Popover
                                    location="top"
                                    content={
                                        "We indiciate timeouts with status 418, we may not have received a response " +
                                        "from your server"
                                    }
                                    disabled={logRow.status !== 418}
                                >
                                    {(ref, getProps) => (
                                        <div
                                            ref={ref}
                                            style={{ flexBasis: 70 }}
                                            className={
                                                "grow-0 shrink text-center " +
                                                (
                                                    (
                                                        200 <= logRow.status &&
                                                        logRow.status <= 299 &&
                                                        logRow.response_body !== "Response contained a body"
                                                    ) ?
                                                        "status-success" :
                                                        "status-error"
                                                ) +
                                                (logRow.status === 418 ? " cursor-pointer" : "")
                                            }
                                            {...getProps()}
                                        >
                                            {logRow.status}
                                        </div>
                                    )}
                                </Popover>
                                <div style={{ flexBasis: 160 }} className="grow-1 shrink">
                                    {logRow.vendor}
                                </div>
                                <div style={{ flexBasis: 50 }} className="actions grow-0 shrink">
                                    <span
                                        className="action"
                                        onClick={() => {
                                            dispatch(showDialog(
                                                "Call Details",
                                                <ApplicationCallDetails
                                                    log={logRow}
                                                />,
                                                [{
                                                    type   : "primary",
                                                    label  : "Done",
                                                    onClick: () => {
                                                        dispatch(hideDialog());
                                                    },
                                                }],
                                                "normal",
                                                true
                                            ));
                                        }}
                                    >
                                        View
                                    </span>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    }

    return (
        <main id="application" ref={scrollRef} className="pt-0!">
            <h1 className="text-4xl mt-8">
                History
                <Button
                    onClick={() => navigate("/applications")}
                    classes={[ "back-button" ]}
                >
                    Back
                </Button>
            </h1>
            <div>
                {content}
            </div>
        </main>
    );
};
